import { colors } from '@lmc/common/constants'
import { Button } from '@lmc/ui-kit'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { FamilyInviteWizard } from '../../../generic'

interface FamilyPortalCompleteProps {
  fields?: any
  close: () => void
  preferredTerm: string
}

interface InfoSectionCardProps {
  imgUrl: string
  title: any
  subtitle: any
  children?: any
}

const InfoSectionCard = ({
  imgUrl,
  title,
  subtitle,
  children
}: InfoSectionCardProps) => (
  <InfoSectionContainer>
    <StakeHolderContainer>
      <img alt="" src={imgUrl} />
      {title}
      <UnderLine />
    </StakeHolderContainer>
    <PlainText>{subtitle}</PlainText>
    {children}
  </InfoSectionContainer>
)

export default function FamilyPortalComplete({
  preferredTerm,
  close
}: FamilyPortalCompleteProps) {
  const formRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (formRef.current) formRef.current.scrollIntoView()
  }, [])

  // Residents -> resident
  const slicedTerm = preferredTerm.slice(0, -1).toLowerCase()

  const renderLinkContainer = () => (
    <LinkContainer>
      <InfoSectionCard
        imgUrl="https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/Social.png"
        title={preferredTerm}
        subtitle={CLIENT_TEXT(slicedTerm)}
      />
      <InfoSectionCard
        imgUrl="https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/Activies+and+Social.png"
        title="Families"
        subtitle={FAMILY_TEXT}
      >
        <Links
          href="https://support.logmycare.co.uk/en/articles/4583018-announcement-email-template-for-families"
          target="_blank"
          rel="noreferrer"
        >
          {'Email template >'}
        </Links>
      </InfoSectionCard>
      <InfoSectionCard
        imgUrl="https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/Family+Update.png"
        title="Carers"
        subtitle={CARER_TEXT}
      />
    </LinkContainer>
  )

  const renderInitialStepContainer = () => (
    <InitialStepContainer>
      <TextBold>Step 1</TextBold>
      <TextPrimary>Let everyone know</TextPrimary>
      {renderLinkContainer()}
    </InitialStepContainer>
  )

  const renderSecondStepContainer = () => (
    <SecondStepContainer>
      <TextBold>Step 2</TextBold>
      <TextPrimary>Invite family members</TextPrimary>
      <SubHeadingContainer>
        <SubHeading>{FAMILY_INTEREST(slicedTerm)}</SubHeading>
        <br />
        <Button onClick={close} style={{ marginLeft: 15 }} variant="default">
          <SkipButton>
            <img
              alt=""
              src="https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/Skip.svg"
              style={{ marginRight: 5 }}
            />
            Skip for now
          </SkipButton>
        </Button>
      </SubHeadingContainer>
      <FamilyInviteWizard />
    </SecondStepContainer>
  )

  // TODO: Add a tag links
  return (
    <Container>
      <div style={{ height: 80 }} ref={formRef} />
      <Heading>{SETUP_COMPLETE}</Heading>
      <SubHeading>{MODULE_SETUP}</SubHeading>
      {renderInitialStepContainer()}
      {renderSecondStepContainer()}
      <Button onClick={close} variant="default">
        Back to Family App Settings
      </Button>
    </Container>
  )
}

const SETUP_COMPLETE = 'Family App Setup Complete!'
const MODULE_SETUP =
  'Amazing! Your Family App is all set up! Now you can start inviting families to the app.'
const CLIENT_TEXT = (preferredTerm: string) =>
  `Make sure you ask if your ${preferredTerm}s are happy to share their information with family members.`
const FAMILY_TEXT =
  'Let families know about the new family app that is available for them.'
const CARER_TEXT =
  'Important to know that families will be able to access certain logs in real time.'
const FAMILY_INTEREST = (preferredTerm: string) =>
  `Once you know a ${preferredTerm} is happy to share their information and a family member is interested in the App, you can start inviting them straight away.`

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
  width: 90vw;
`

const Heading = styled.div`
  color: ${colors.black};
  font-size: 28px;
  font-weight: 300;
  margin-bottom: 20px;
`

const InfoSectionContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 33%;
  padding: 10px;
`

const InitialStepContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 30px;
  width: 50%;
`

const LinkContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const Links = styled.a`
  padding-top: 5px;
`

const PlainText = styled.div`
  font-size: 12px;
  text-align: center;
`

const SecondStepContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 30px;
  width: 70%;
`

const SkipButton = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 2px;
`

const StakeHolderContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 5px;
  padding: 5px;
`

const SubHeading = styled.div`
  color: ${colors.black};
  font-size: 12px;
  font-weight: 400;
`

const SubHeadingContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 30px;
  width: 100%;
`

const TextBold = styled.h5`
  font-size: 14px;
  font-weight: 700;
`

const TextPrimary = styled.h4`
  color: ${colors.pink};
  font-size: 16px;
  font-weight: 400;
`

const UnderLine = styled.div`
  background-color: ${colors.black};
  height: 1px;
  width: 100%;
`
